import { SpecialOfferProps } from '../types';
import { Box, Stack } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useEffect, useMemo, useRef, useState } from 'react';
import { moveProductGrid } from 'utils';
import { BabaSaleBadge } from 'components/Badge/Sale/BabaSaleBadge';
import { BabaBundlePrice } from 'templates/Bundles/Baba/BundlePrice';
import { BabaMainProduct } from 'templates/Bundles/Baba/BundleProduct/main';
import { EProductPriority } from 'utils/enums';
import { ProductData } from 'utils/types';
import { StyledBabaSpecialOffer, StyledPriceSection } from './style';
import { babaUtils } from 'templates/Bundles/Baba/utils';
import { BabaTimerBadge } from './TimerBadge';
import { SubProductsContainer } from 'templates/Bundles/Baba/BundleProduct/SubProductsContainer';

export const BabaSpecialOffer: React.FC<SpecialOfferProps> = ({
  price,
  backgroundImage = 'https://media-staging.appcharge.com/media/660148877b8ae986b7b6379e/1726130855000__70f02c58-e326-44cf-8418-8031376aca79',
  productsList,
  salePercentage = 0,
  buttonTextColor,
  buttonColor,
  inMobileMock = false,
  selectOffer,
  availability,
  availabilityText = 'Available',
  isFree = false,
  collectText = 'Collect',
  timer,
  translations = { includes: 'Includes' },
}) => {
  const subItemsGrid = useRef<HTMLDivElement>(null);
  const [isDoneScrolling, setIsDoneScrolling] = useState(false);
  const [showScroller, setShowScroller] = useState(false);
  const [mainProduct, setMainProduct] = useState<ProductData | null>(null);
  const [subProductsSplit, setSubProductsSplit] = useState<ProductData[][]>([]);

  useEffect(() => {
    if (!productsList || productsList.length === 0) return;

    const main = productsList.find(
      (product) => product.priority === EProductPriority.MAIN,
    );
    setMainProduct(main!);

    const arrSubProducts = productsList.filter(
      (product) => product.priority === EProductPriority.SUB,
    );

    setSubProductsSplit(babaUtils.splitSubProductsArray(arrSubProducts));
  }, [productsList]);

  const moveGrid = (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e?.stopPropagation();

    const subItemsGridElement = subItemsGrid?.current;
    if (subItemsGridElement as HTMLDivElement) {
      moveProductGrid({
        subItemsGridElement: subItemsGridElement as HTMLDivElement,
        isDoneScrolling,
        setIsDoneScrolling,
        distance: (subItemsGridElement as HTMLDivElement).clientWidth,
      });
    }
  };

  useEffect(() => {
    const shouldShowScrollbar = () => {
      setShowScroller(
        subItemsGrid?.current
          ? subItemsGrid?.current?.scrollWidth >
              subItemsGrid?.current?.clientWidth
          : false,
      );
    };

    shouldShowScrollbar();

    window.addEventListener('resize', shouldShowScrollbar);

    return () => {
      window.removeEventListener('resize', shouldShowScrollbar);
    };
  }, [subItemsGrid.current, subProductsSplit]);

  const availabilityDisplayText = useMemo(
    () =>
      availability
        ? `${availabilityText} ${availability.remaining}/${availability.total}`
        : '',
    [availability],
  );

  return (
    <StyledBabaSpecialOffer
      backgroundSrc={backgroundImage}
      className={
        'baba-promotion' + (inMobileMock ? ' bundle--iphone-mock' : '')
      }
      onClick={selectOffer}
    >
      <Box className="container" gap="8px">
        {salePercentage > 0 && (
          <BabaSaleBadge salePercentage={salePercentage} />
        )}
        {productsList.length > 0 ? (
          <Box className="products">
            {showScroller && (
              <Stack className="scroller">
                <button onClick={(e) => moveGrid(e)}>
                  {isDoneScrolling ? (
                    <ArrowBackIosNewIcon
                      sx={{
                        color: 'white',
                        fontSize: 16,
                      }}
                    />
                  ) : (
                    <ArrowForwardIosIcon
                      sx={{
                        color: 'white',
                        fontSize: 16,
                      }}
                    />
                  )}
                </button>
              </Stack>
            )}
            <BabaMainProduct
              quantityDisplay={mainProduct?.quantityDisplay}
              strokedSaleQuantity={mainProduct?.strokedSaleQuantity}
              image={mainProduct?.images?.product}
              salePercentage={salePercentage}
            />
            {subProductsSplit && subProductsSplit.length > 0 && (
              <SubProductsContainer
                subItemsGridRef={subItemsGrid}
                subProductsSplit={subProductsSplit}
              />
            )}
          </Box>
        ) : (
          <Box className="products" style={{ height: '76px' }}></Box>
        )}
        <StyledPriceSection>
          <BabaTimerBadge timer={timer} />
          <BabaBundlePrice
            price={price}
            bundleButtonColor={buttonColor}
            bundleButtonTextColor={buttonTextColor}
            isFree={isFree}
            collectText={collectText}
          />
          <div className={'availability-label'}>
            {availabilityDisplayText ? availabilityDisplayText : ''}
          </div>
        </StyledPriceSection>
      </Box>
    </StyledBabaSpecialOffer>
  );
};
